<template>
  <div class="tag-chips-editor">
    <div class="panel panel-default">
      <div class="panel-heading">
        <h4 class="panel-title">
          <i class="fa fa-tags"></i> Markierungen
        </h4>
      </div>
      <div class="panel-body">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
            <tr>
              <th width="60">#</th>
              <th>Bezeichnung</th>
              <th>Wert</th>
              <th width="40"></th>
              <th width="80">Aktionen</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="(item, index) in items"
                :key="item.id"
                :class="{ 'dragging': draggingIndex === index }"
                class="tag-chip-row"
            >
              <td class="text-center">
                <span class="badge badge-primary">{{ index + 1 }}</span>
              </td>
              <td>
                <input
                    v-model="item.name"
                    :class="{ 'has-error': !item.name.trim() }"
                    class="form-control input-sm"
                    placeholder="Markierungsbezeichnung eingeben"
                    type="text"
                    @input="updateValue"
                />
              </td>
              <td style="position: relative;">
                <input
                    v-model="item.value"
                    :class="{ 'has-error': !item.value.trim() }"
                    class="form-control input-sm"
                    placeholder="Markierungswert eingeben, @ für Variablen"
                    type="text"
                    @blur="hideSuggestions"
                    @input="handleValueInput(index, $event)"
                    @keydown.esc="hideSuggestions"
                />
                <div v-if="activeAutocompleteIndex === index && filteredSuggestions.length > 0"
                     class="autocomplete-suggestions">
                  <ul>
                    <li
                        v-for="suggestion in filteredSuggestions"
                        :key="suggestion.key"
                        @mousedown.prevent="selectSuggestion(index, suggestion.key)"
                    >
                      <strong>{{ suggestion.key }}</strong>
                      <small class="text-muted" style="margin-left: 8px;">{{ suggestion.value }}</small>
                    </li>
                  </ul>
                </div>
              </td>
              <td class="text-center">
                <button
                    class="btn btn-success btn-xs drag-handle"
                    style="margin-right:4px;"
                    title="Drag to reorder"
                    type="button"
                    @mousedown="startDrag(index, $event)"
                >
                  <i class="fa fa-sort"></i>
                </button>
              </td>
              <td class="text-center">
                <button
                    class="btn btn-danger btn-xs"
                    title="Delete this tag"
                    type="button"
                    @click="removeItem(index)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="text-center" style="margin-top: 15px;">
          <button
              class="btn btn-success btn-sm"
              type="button"
              @click="addItem"
          >
            <i class="fa fa-plus"></i> Neue Markierung hinzufügen
          </button>
        </div>

        <input
            :name="fieldName"
            :value="jsonValue"
            type="hidden"
        />
      </div>
    </div>
  </div>
</template>

<script>
import variables from './variables.json';
export default {
  name: 'LoyaltyPointScanDetailViewTagChips',

  props: {
    fieldName: {
      type: String,
      default: 'tag_chips'
    },
    initialValue: {
      type: [String, Array],
      default: () => []
    }
  },

  data() {
    return {
      items: [],
      draggingIndex: -1,
      dragStartY: 0,
      dragStartIndex: -1,
      activeAutocompleteIndex: null,
      filteredSuggestions: [],
      allVariables: variables
    }
  },

  computed: {
    jsonValue() {
      const validItems = this.items.filter(item =>
          item.name.trim() !== '' && item.value.trim() !== ''
      );
      return JSON.stringify(
          validItems.map(item => ({name: item.name, value: item.value}))
      );
    }
  },

  watch: {
    initialValue: {
      handler() {
        this.initializeItems();
      },
      immediate: true
    }
  },

  methods: {
    handleValueInput(index, event) {
      const value = event.target.value;
      const cursorPosition = event.target.selectionStart;
      const lastAt = value.lastIndexOf('@', cursorPosition - 1);
      if (lastAt !== -1) {
        const textAfterAt = value.substring(lastAt + 1, cursorPosition);
        if (textAfterAt.includes(' ')) {
          this.hideSuggestions(true);
          this.updateValue();
          return;
        }

        this.activeAutocompleteIndex = index;
        this.filteredSuggestions = this.allVariables.filter(v =>
            v.key.toLowerCase().includes(textAfterAt.toLowerCase())
        );
      } else {
        this.hideSuggestions(true);
      }

      this.updateValue();
    },

    selectSuggestion(index, selectedKey) {
      const currentItem = this.items[index];
      const value = currentItem.value;
      const cursorPosition = document.activeElement.selectionStart;

      const lastAt = value.lastIndexOf('@', cursorPosition - 1);

      if (lastAt !== -1) {
        const prefix = value.substring(0, lastAt);
        const suffix = value.substring(cursorPosition);
        currentItem.value = prefix + selectedKey + ' ' + suffix;
      }

      this.hideSuggestions(true);
      this.updateValue();
    },

    hideSuggestions(force = false) {
      const delay = force ? 0 : 200;
      setTimeout(() => {
        this.activeAutocompleteIndex = null;
        this.filteredSuggestions = [];
      }, delay);
    },

    generateId() {
      return Date.now() + Math.random().toString(36).substr(2, 9);
    },

    initializeItems() {
      let initialData = [];

      if (typeof this.initialValue === 'string') {
        try {
          initialData = JSON.parse(this.initialValue) || [];
        } catch (e) {
          initialData = [];
        }
      } else if (Array.isArray(this.initialValue)) {
        initialData = this.initialValue;
      }

      if (initialData.length === 0) {
        this.items = [{id: this.generateId(), name: '', value: ''}];
      } else {
        this.items = initialData.map(item => ({
          id: this.generateId(),
          name: item.name || '',
          value: item.value || ''
        }));
      }
    },

    addItem() {
      this.items.push({id: this.generateId(), name: '', value: ''});
    },

    removeItem(index) {
      if (this.items.length > 1) {
        this.items.splice(index, 1);
      } else {
        this.items = [{id: this.generateId(), name: '', value: ''}];
      }
      this.updateValue();
    },

    updateValue() {
      this.$nextTick(() => {
        const inputEvent = new Event('input', {bubbles: true});
        const hiddenInput = document.querySelector(`input[name="${this.fieldName}"]`);
        if (hiddenInput) {
          hiddenInput.dispatchEvent(inputEvent);
        }
        const chipsArray = JSON.parse(this.jsonValue);
        const chipsEvent = new CustomEvent('tag-chips-updated', {detail: chipsArray});
        window.dispatchEvent(chipsEvent);
      });
    },

    startDrag(index, event) {
      this.draggingIndex = index;
      this.dragStartIndex = index;
      this.dragStartY = event.clientY;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.endDrag);
      event.preventDefault();
    },

    onDrag(event) {
      if (this.draggingIndex === -1) return;
      const deltaY = event.clientY - this.dragStartY;
      const rowHeight = 60;
      const newIndex = Math.max(0, Math.min(this.items.length - 1, this.dragStartIndex + Math.floor(deltaY / rowHeight)));
      if (newIndex !== this.draggingIndex) {
        const item = this.items[this.draggingIndex];
        this.items.splice(this.draggingIndex, 1);
        this.items.splice(newIndex, 0, item);
        this.draggingIndex = newIndex;
      }
    },

    endDrag() {
      this.draggingIndex = -1;
      this.dragStartIndex = -1;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      this.updateValue();
    }
  },

  mounted() {
    this.initializeItems();
  }
}
</script>

<style scoped>
.autocomplete-suggestions {
  position: absolute;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  width: 95%;
  left: 2.5%;
  top: 100%;
  margin-top: 2px;
}

.autocomplete-suggestions ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.autocomplete-suggestions li {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.autocomplete-suggestions li:last-child {
  border-bottom: none;
}

.autocomplete-suggestions li:hover {
  background-color: #f8f9fa;
}

.tag-chips-editor {
  margin: 15px 0;
}

.tag-chip-row {
  transition: all 0.2s ease;
}

.tag-chip-row:hover {
  background-color: #f8f9fa;
}

.tag-chip-row.dragging {
  opacity: 0.7;
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.drag-handle {
  cursor: move;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.drag-handle:hover {
  background-color: #e9ecef;
}

.drag-handle:active {
  background-color: #dee2e6;
}

.has-error {
  border-color: #66afe9 !important;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(102, 175, 233, .6) !important;
}

.panel-heading .fa {
  margin-right: 8px;
}

.table > tbody > tr > td {
  vertical-align: middle;
}

.badge-primary {
  background-color: #337ab7;
}

.btn-xs {
  padding: 1px 5px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 3px;
}

.btn-success {
  background-color: #5cb85c;
  border-color: #4cae4c;
}

.btn-success:hover {
  background-color: #449d44;
  border-color: #398439;
}

.btn-danger {
  background-color: #d9534f;
  border-color: #d43f3a;
}

.btn-danger:hover {
  background-color: #c9302c;
  border-color: #ac2925;
}

.text-muted {
  color: #777;
}

.form-control:focus {
  border-color: #66afe9;
  outline: 0;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(102, 175, 233, .6);
}

.tag-chips-editor .table-responsive {
  overflow: visible !important;
}

.tag-chips-editor .panel-body {
  overflow: visible;
}

.autocomplete-suggestions {
  z-index: 9999 !important;
}

.autocomplete-suggestions li small.text-muted {
  display: none;
}
</style>
